// Phone-agent KPI scorecard. Black-and-gold per the brief; weighted
// toward conversion + speed (the ratios that actually predict
// performance) over activity (dial counts, which reward looking
// busy).
//
// Layout (top → bottom):
//   1. Hero card: 5 headline KPIs with target + delta vs previous
//      window
//   2. Activity strip (diagnostic): dials / connects / talk time /
//      appointments set / shows / closed won
//   3. Conversion ratios (diagnostic): contact rate / appt set rate /
//      close rate / lead-to-sale / calls per sale
//   4. Quality strip: bad data % + disposition breakdown
//
// Time-window filter: today / week / month
//
// Reads /api/scorecard/agent-kpis. No write actions.

import React from 'react';
import { Phone, Zap, Target, Award, DollarSign, TrendingUp, AlertCircle, Clock } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useIsDemoMode } from '../demo/DemoDataProvider';

// Demo Mode scorecard — believable phone-agent KPIs for a healthy
// week of selling. Numbers chosen to hit a couple targets (so amber
// tile highlights light up) and miss a couple (so the "where you'd
// coach" copy makes sense). All shape matches /api/scorecard/agent-kpis.
function demoScorecard(windowKey) {
  const SCALE = windowKey === 'today' ? 1 : windowKey === 'week' ? 6 : 25;
  return {
    window: windowKey,
    headline: {
      speed_to_lead: {
        median_seconds: 47,
        sample_size:    Math.round(11 * SCALE),
        pct_under_1min: 0.62,
      },
      contact_rate:          0.41,
      connect_to_appointment:0.34,
      show_rate:             0.78,
      close_rate:            0.36,
      revenue_cents:         Math.round(284_000 * SCALE),
    },
    activity: {
      dials:                Math.round(58 * SCALE),
      connects:             Math.round(24 * SCALE),
      // Live reads `talk_time_sec` (line 288 DiagTile) — NOT `talk_time_seconds`.
      talk_time_sec:        Math.round(1820 * SCALE),
      appointments_set:     Math.round(8 * SCALE),
      shows:                Math.round(6 * SCALE),
      closed_won:           Math.round(2 * SCALE),
      // Live reads this on the quality strip (line 327). Missing →
      // "—" displays instead of a number; non-crashing but ugly.
      follow_ups_promised:  Math.round(7 * SCALE),
    },
    ratios: {
      contact_rate:            0.41,
      appointment_set_rate:    0.34,
      connect_to_appointment:  0.34,
      show_rate:               0.78,
      close_rate:              0.36,
      // Live reads `lead_to_sale_rate` on the quality strip (line 333).
      lead_to_sale_rate:       0.14,
      calls_per_sale:          28,
    },
    quality: {
      bad_data_pct:    0.06,
      bad_data_count:  Math.round(5 * SCALE),
      // Live code at AgentScorecardView.jsx:341 iterates this with
      // Object.entries() expecting a { key: count } MAP, not an
      // array of {key,label,count} objects. Returning an array
      // makes Object.entries yield [index, object] pairs → the
      // .map tries to render the whole object as a child → React
      // error #31 on mount. Shape is a flat map per disposition key.
      dispositions: {
        appt_set:        Math.round(8  * SCALE),
        callback:        Math.round(11 * SCALE),
        voicemail:       Math.round(18 * SCALE),
        no_answer:       Math.round(14 * SCALE),
        wrong_number:    Math.round(3  * SCALE),
        not_interested:  Math.round(4  * SCALE),
      },
    },
    is_demo: true,
  };
}

// Read JWT directly from localStorage — bypasses supabase.auth.getSession()
// which can hang mid-refresh on a stale token. That hang is why the
// scorecard sat on "Loading scorecard..." forever with no error surfaced.
// Same pattern as src/leads/authedFetch.js and src/referrals/ReferralTreeView.jsx.
function readPersistedJwtDirect() {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith('sb-') || !k.endsWith('-auth-token')) continue;
      const raw = localStorage.getItem(k);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      const token = parsed?.access_token || parsed?.currentSession?.access_token;
      if (!token) continue;
      const payload = JSON.parse(atob(token.split('.')[1] || ''));
      if (payload?.exp && payload.exp * 1000 > Date.now() + 60_000) return token;
    }
  } catch { /* fall through */ }
  return null;
}

async function apiFetch(path, init = {}, signal = null) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...init,
    signal,
    headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}), ...(init.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
}

function fmtPct(v) {
  if (v == null || !Number.isFinite(v)) return '—';
  return `${Math.round(v * 100)}%`;
}
function fmtNum(v) {
  if (v == null || !Number.isFinite(v)) return '—';
  return v.toLocaleString();
}
function fmtUSD(cents) {
  if (!cents) return '$0';
  return `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}
function fmtDuration(seconds) {
  // Bug fix: 0 is a legitimate value ("no calls today" → 0s),
  // not a "missing data" sentinel. `!seconds` was treating 0 as
  // missing and rendering "—" which contradicted the other tiles
  // showing literal 0. Explicit null/NaN guard preserves the
  // "—" for actually-missing data.
  if (seconds == null || !Number.isFinite(seconds)) return '—';
  if (seconds === 0) return '0s';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  if (mins < 60) return secs ? `${mins}m ${secs}s` : `${mins}m`;
  const hours = Math.floor(mins / 60);
  const remMin = mins % 60;
  return remMin ? `${hours}h ${remMin}m` : `${hours}h`;
}

// Targets — defensible heuristics. Operator can tune later via Settings.
const TARGETS = {
  speed_to_lead_sec:      60,    // < 1 minute
  contact_rate:           0.30,  // 30%
  connect_to_appointment: 0.35,  // 35%
  show_rate:              0.60,  // 60%
  revenue_today_cents:    50000, // $500/day
  revenue_week_cents:     250000,// $2500/wk
  revenue_month_cents:    1000000,// $10k/mo
};

function revenueTargetFor(window) {
  if (window === 'today') return TARGETS.revenue_today_cents;
  if (window === 'week')  return TARGETS.revenue_week_cents;
  if (window === 'month') return TARGETS.revenue_month_cents;
  return TARGETS.revenue_today_cents;
}

export default function AgentScorecardView({ currentUser, showToast = () => {} }) {
  const isDemo = useIsDemoMode();
  const [windowKey, setWindowKey] = React.useState('today');
  const [data, setData]           = React.useState(null);
  const [loading, setLoading]     = React.useState(true);
  const [error, setError]         = React.useState(null);
  const [retryNonce, setRetryNonce] = React.useState(0);

  React.useEffect(() => {
    // If currentUser is still resolving, stay in loading state but
    // arm a 3s watchdog so a permanently-missing user resolves to a
    // signed-out message instead of an infinite spinner.
    if (!currentUser?.id) {
      const t = setTimeout(() => {
        setLoading(false);
        setError('Sign in required to see your scorecard.');
      }, 3000);
      return () => clearTimeout(t);
    }
    if (isDemo) {
      setData(demoScorecard(windowKey));
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 12_000);
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const r = await apiFetch(`/api/scorecard/agent-kpis?window=${windowKey}`, {}, controller.signal);
        if (!cancelled) { setData(r); setError(null); }
      } catch (err) {
        if (cancelled) return;
        const msg = err?.name === 'AbortError' ? 'request_timed_out' : (err?.message || 'load_failed');
        setError(msg);
        showToast(`Scorecard load failed: ${msg}`, 'error');
      } finally {
        clearTimeout(timer);
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; controller.abort(); clearTimeout(timer); };
  }, [currentUser?.id, windowKey, showToast, isDemo, retryNonce]);

  if (loading) {
    return (
      <div className="max-w-none mx-auto px-4 py-4">
        <div className="text-sm text-slate-500">Loading scorecard…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="max-w-none mx-auto px-4 py-4">
        <div className="bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 text-sm text-amber-900">
          Scorecard temporarily unavailable ({error}).
          <button
            onClick={() => { setError(null); setLoading(true); setData(null); setRetryNonce(n => n + 1); }}
            className="ml-3 text-amber-800 underline hover:text-amber-900 font-semibold"
          >Retry</button>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="max-w-none mx-auto px-4 py-4">
        <div className="text-sm text-slate-500">No scorecard data yet.</div>
      </div>
    );
  }

  const h = data.headline || {};
  const act = data.activity || {};
  const ratios = data.ratios || {};
  const quality = data.quality || {};

  const speedSec      = h.speed_to_lead?.median_seconds ?? null;
  const speedSample   = h.speed_to_lead?.sample_size    ?? 0;
  const speedHit      = speedSec != null && speedSec <= TARGETS.speed_to_lead_sec;
  const contactHit    = h.contact_rate != null && h.contact_rate >= TARGETS.contact_rate;
  const connApptHit   = h.connect_to_appointment != null && h.connect_to_appointment >= TARGETS.connect_to_appointment;
  const showHit       = h.show_rate != null && h.show_rate >= TARGETS.show_rate;
  const revTarget     = revenueTargetFor(windowKey);
  const revHit        = h.revenue_cents != null && h.revenue_cents >= revTarget;

  const headlines = [
    {
      key: 'speed', icon: Zap, label: 'Speed-to-lead',
      value: speedSec != null ? fmtDuration(speedSec) : '—',
      target: `target ≤ ${fmtDuration(TARGETS.speed_to_lead_sec)}`,
      hit: speedHit,
      diag: speedSample > 0 ? `${Math.round((h.speed_to_lead.pct_under_1min || 0) * 100)}% under 1 min · n=${speedSample}` : 'No new leads in window',
      hint: 'The single most predictive number. Call inside 60 seconds.',
    },
    {
      key: 'contact', icon: Phone, label: 'Contact rate',
      value: fmtPct(h.contact_rate),
      target: `target ≥ ${fmtPct(TARGETS.contact_rate)}`,
      hit: contactHit,
      diag: `${fmtNum(act.connects)} connects / ${fmtNum(act.dials)} dials`,
      hint: 'Low rate? Bad data, bad timing, or flagged caller ID.',
    },
    {
      key: 'connToAppt', icon: Target, label: 'Connect → appt',
      value: fmtPct(h.connect_to_appointment),
      target: `target ≥ ${fmtPct(TARGETS.connect_to_appointment)}`,
      hit: connApptHit,
      diag: `${fmtNum(act.appointments_set)} appts / ${fmtNum(act.connects)} connects`,
      hint: 'Pure measure of the opener + pitch — strip volume out.',
    },
    {
      key: 'show', icon: Award, label: 'Show rate',
      value: fmtPct(h.show_rate),
      target: `target ≥ ${fmtPct(TARGETS.show_rate)}`,
      hit: showHit,
      diag: `${fmtNum(act.shows)} shows of resolved appts`,
      hint: 'Booking junk to hit quota wrecks this — confirm + remind.',
    },
    {
      key: 'rev', icon: DollarSign, label: 'Revenue',
      value: fmtUSD(h.revenue_cents),
      target: `target ${fmtUSD(revTarget)}`,
      hit: revHit,
      diag: `${fmtNum(act.closed_won)} closed-won`,
      hint: 'Premium written. The number that actually matters.',
    },
  ];

  return (
    <div className="max-w-none mx-auto px-4 py-4 space-y-4">

      {/* Hero — black + gold. */}
      <div className="bg-gradient-to-br from-slate-950 to-black text-white rounded-xl overflow-hidden border border-amber-700/20">
        <div className="px-5 py-4 border-b border-amber-900/30 flex items-baseline justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-amber-400/80 flex items-center gap-2">
              <TrendingUp size={11}/> Phone agent scorecard
            </div>
            <div className="text-lg font-bold mt-1">
              {windowKey === 'today' ? 'Today' : windowKey === 'week' ? 'Last 7 days' : 'Last 30 days'}
            </div>
          </div>
          <div className="flex items-center gap-1 bg-black/60 border border-amber-900/40 rounded-full p-0.5">
            {['today', 'week', 'month'].map(k => (
              <button
                key={k}
                onClick={() => setWindowKey(k)}
                className={`text-[11px] uppercase tracking-wider font-bold px-3 py-1 rounded-full transition-colors ${
                  windowKey === k
                    ? 'bg-amber-500 text-black'
                    : 'text-amber-200/60 hover:text-amber-100'
                }`}
              >
                {k === 'today' ? 'Today' : k === 'week' ? 'Week' : 'Month'}
              </button>
            ))}
          </div>
        </div>

        {/* 5 headline tiles */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-amber-900/20">
          {headlines.map(({ key, icon: Icon, label, value, target, hit, diag, hint }) => (
            <div key={key} className="bg-gradient-to-br from-slate-950 to-black px-4 py-5 relative overflow-hidden">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-amber-400/70">
                <Icon size={10}/> {label}
              </div>
              <div className={`text-3xl font-black mt-2 tabular-nums ${hit ? 'text-amber-400' : 'text-white'}`}>
                {value}
              </div>
              <div className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
                {hit ? <span className="text-amber-400">●</span> : <span className="text-slate-600">○</span>}
                {target}
              </div>
              <div className="text-[10px] text-slate-500 mt-1.5 leading-snug">{diag}</div>
              <div className="text-[10px] text-amber-300/40 mt-2 leading-snug italic">{hint}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Activity strip — the work (don't judge on these alone) */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2"><Clock size={14} className="text-slate-400"/> Activity</h3>
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Did the work get done?</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-slate-100">
          <DiagTile label="Dials"             value={fmtNum(act.dials)}/>
          <DiagTile label="Connects"          value={fmtNum(act.connects)}/>
          <DiagTile label="Talk time"         value={fmtDuration(act.talk_time_sec)}/>
          <DiagTile label="Appts set"         value={fmtNum(act.appointments_set)}/>
          <DiagTile label="Shows"             value={fmtNum(act.shows)}/>
          <DiagTile label="Closed-won"        value={fmtNum(act.closed_won)}/>
        </div>
      </div>

      {/* Ratios — the conversion (where greatness shows) */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2"><Target size={14} className="text-amber-600"/> Conversion ratios</h3>
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Where the work paid off</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-slate-100">
          <DiagTile label="Contact rate"        value={fmtPct(ratios.contact_rate)}        sub="connects / dials"/>
          <DiagTile label="Appt set rate"       value={fmtPct(ratios.appointment_set_rate)} sub="appts / dials"/>
          <DiagTile label="Connect → appt"      value={fmtPct(ratios.connect_to_appointment)} sub="appts / connects"/>
          <DiagTile label="Show rate"           value={fmtPct(ratios.show_rate)}            sub="shows / resolved"/>
          <DiagTile label="Close rate"          value={fmtPct(ratios.close_rate)}           sub="closed / shows"/>
          <DiagTile label="Calls / sale"        value={ratios.calls_per_sale != null ? Math.round(ratios.calls_per_sale).toLocaleString() : '—'} sub="efficiency"/>
        </div>
      </div>

      {/* Quality / disposition diagnostics */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2"><AlertCircle size={14} className="text-rose-600"/> Quality &amp; dispositions</h3>
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Coach the weak link</div>
        </div>
        <div className="px-5 py-3">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
            <DiagTileFlat
              label="Bad data signal"
              value={`${quality.bad_data_count || 0} calls`}
              sub={`${fmtPct(quality.bad_data_pct)} of dials — high = bad list or wrong-number dump`}
              tone={quality.bad_data_pct > 0.10 ? 'red' : 'slate'}
            />
            <DiagTileFlat
              label="Follow-ups promised"
              value={fmtNum(act.follow_ups_promised)}
              sub="Broken follow-ups are pure leaked revenue — match against later closes"
              tone="amber"
            />
            <DiagTileFlat
              label="Lead → sale"
              value={fmtPct(ratios.lead_to_sale_rate)}
              sub="Whole funnel in one number"
              tone="slate"
            />
          </div>
          <div className="border border-slate-100 rounded-lg overflow-hidden">
            <div className="px-3 py-2 border-b border-slate-100 text-[10px] uppercase tracking-wider font-bold text-slate-500 bg-slate-50">
              Disposition breakdown
            </div>
            {Object.keys(quality.dispositions || {}).length === 0 ? (
              <div className="px-3 py-3 text-xs text-slate-500">
                No dispositions logged in window. Disposition accuracy = whether the agent is logging outcomes honestly. Missing dispositions poison every ratio above.
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-slate-100">
                {Object.entries(quality.dispositions || {})
                  .sort((a, b) => b[1] - a[1])
                  .map(([d, n]) => (
                    <div key={d} className="bg-white px-3 py-2 text-center">
                      <div className="text-base font-bold tabular-nums text-slate-900">{n}</div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mt-0.5">{d.replace(/_/g, ' ')}</div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Coaching footer — translate ratios → action */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl px-5 py-3">
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">Where to coach</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-slate-700">
          <CoachLine
            hit={contactHit}
            okText="Contact rate is on target."
            failText="Contact rate is low → data, timing, or flagged caller ID. Pull list source first."
          />
          <CoachLine
            hit={connApptHit}
            okText="Connect → appointment is on target."
            failText="Connecting but not booking → opener / pitch. Listen to the next 5 connects and coach the first 15 seconds."
          />
          <CoachLine
            hit={showHit}
            okText="Show rate is on target."
            failText="Setting but not showing → confirmation process. Add a t-1 day SMS and a t-2 hour SMS."
          />
          <CoachLine
            hit={revHit}
            okText={`On pace — ${fmtUSD(h.revenue_cents)} written, target ${fmtUSD(revTarget)}.`}
            failText={`Off pace — ${fmtUSD(h.revenue_cents)} written, need ${fmtUSD(revTarget)}. Look at the weak ratio above.`}
          />
        </div>
      </div>
    </div>
  );
}

function DiagTile({ label, value, sub }) {
  return (
    <div className="bg-white px-4 py-3 text-center">
      <div className="text-xl font-bold tabular-nums text-slate-900">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mt-0.5">{label}</div>
      {sub && <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function DiagTileFlat({ label, value, sub, tone = 'slate' }) {
  const tones = {
    slate:  'border-slate-200 bg-slate-50    text-slate-900',
    amber:  'border-amber-200 bg-amber-50    text-amber-900',
    red:    'border-red-200   bg-red-50      text-red-900',
  };
  return (
    <div className={`border rounded-lg px-3 py-2.5 ${tones[tone]}`}>
      <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">{label}</div>
      <div className="text-lg font-bold tabular-nums mt-0.5">{value}</div>
      <div className="text-[10px] opacity-70 mt-0.5 leading-snug">{sub}</div>
    </div>
  );
}

function CoachLine({ hit, okText, failText }) {
  return (
    <div className={`px-2 py-1.5 rounded ${hit ? 'bg-emerald-50 border border-emerald-200 text-emerald-900' : 'bg-amber-50 border border-amber-200 text-amber-900'}`}>
      <span className="font-semibold">{hit ? '✓ ' : '→ '}</span>
      {hit ? okText : failText}
    </div>
  );
}
